import { useEffect, useMemo, useState } from "react";

import { api, apiErrorDetail, type AdminJiraSettings } from "../../../lib/api";
import { JiraCapsFields } from "./JiraCapsFields";
import { JiraConnectionPanel } from "./JiraConnectionPanel";
import {
  invalidCaps,
  MAX_ALLOWED_PROJECTS,
  parseProjectKeys,
  settingsPatch,
  type JiraSettingsForm,
} from "./jiraSettings";

/** Settings tab: connection check, kill switch, the project allowlist, and caps. */
export function JiraSettingsTab() {
  const [saved, setSaved] = useState<AdminJiraSettings | null>(null);
  // The allowlist is edited as text (`projectsText`), so the form holds the rest.
  const [form, setForm] = useState<Omit<
    JiraSettingsForm,
    "allowed_projects"
  > | null>(null);
  const [projectsText, setProjectsText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** Load the server's settings into both the saved snapshot and the form. */
  function hydrate(s: AdminJiraSettings) {
    setSaved(s);
    setForm({
      enabled: s.enabled,
      max_issues_per_query: s.max_issues_per_query,
      per_user_queries_per_hour: s.per_user_queries_per_hour,
      default_cache_ttl_seconds_cap: s.default_cache_ttl_seconds_cap,
    });
    setProjectsText(s.allowed_projects.join(", "));
  }

  /** Fetch the settings; a failure renders below with a Retry button. */
  function load() {
    setError(null);
    api.admin.jira.settings
      .get()
      .then(hydrate)
      .catch((e: unknown) => setError(apiErrorDetail(e)));
  }

  useEffect(() => {
    load();
  }, []);

  const parsed = useMemo(() => parseProjectKeys(projectsText), [projectsText]);
  const patch = useMemo(
    () =>
      saved && form
        ? settingsPatch(saved, { ...form, allowed_projects: parsed.keys })
        : {},
    [saved, form, parsed],
  );
  const invalid = form ? invalidCaps(form) : [];
  const tooManyProjects = parsed.keys.length > MAX_ALLOWED_PROJECTS;
  // Invalid tokens leave `patch` unchanged but still differ from what was saved.
  const dirty = Object.keys(patch).length > 0 || parsed.invalid.length > 0;
  const canSave =
    !saving &&
    parsed.invalid.length === 0 &&
    !tooManyProjects &&
    invalid.length === 0 &&
    Object.keys(patch).length > 0;

  /** PUT only the changed fields, then re-hydrate from the server's answer. */
  async function save() {
    setSaving(true);
    setError(null);
    try {
      hydrate(await api.admin.jira.settings.put(patch));
    } catch (e) {
      setError(apiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  }

  if (!saved || !form) {
    return error ? (
      <p className="text-sm text-rose-700">
        {error}{" "}
        <button type="button" onClick={load} className="underline">
          Retry
        </button>
      </p>
    ) : (
      <p className="text-slate-500">Loading…</p>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      {/* Keyed on the saved allowlist: a save that changes it re-runs the check. */}
      <JiraConnectionPanel key={saved.allowed_projects.join(",")} />

      <label className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
        <span>
          <span className="block text-sm font-semibold">
            Jira proxy enabled
          </span>
          <span className="text-xs text-slate-500">
            Kill switch for window.jira and query_jira; applies to the next
            request.
          </span>
        </span>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
      </label>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-1 text-sm font-semibold">Allowlisted projects</h2>
        <p className="mb-2 text-xs text-amber-800">
          Adding a project makes every issue in it — including descriptions and
          comments — readable by every signed-in DataCamper. Never add security,
          HR or service-desk projects.
        </p>
        <textarea
          aria-label="Allowlisted project keys"
          value={projectsText}
          onChange={(e) => setProjectsText(e.target.value)}
          placeholder="ENG, OPS"
          rows={2}
          className="w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm"
        />
        {parsed.invalid.length > 0 && (
          <p className="mt-1 text-xs text-rose-700">
            Not valid Jira project keys: {parsed.invalid.join(", ")}
          </p>
        )}
        {tooManyProjects && (
          <p className="mt-1 text-xs text-rose-700">
            At most {MAX_ALLOWED_PROJECTS} projects ({parsed.keys.length}{" "}
            entered).
          </p>
        )}
        {parsed.keys.length === 0 && (
          <p className="mt-1 text-xs text-amber-700">
            Empty allowlist — every Jira query is denied.
          </p>
        )}
      </div>

      <JiraCapsFields
        caps={form}
        invalid={invalid}
        onChange={(caps) => setForm({ ...form, ...caps })}
      />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            hydrate(saved);
            setError(null);
          }}
          disabled={!dirty || saving}
          className="rounded-md border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
